import type React from 'react';

import { Button, ChangeDot, Text } from '@change/ds-core-ui';
import { StopCircleIcon } from '@change/ds-icons';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { ErrorMessage } from '../ErrorMessage';
import { TermsDisclaimer } from '../TermsDisclaimer';

import { ChooseVideoButton } from './components/ChooseVideoButton';
import { RecorderTimeLimit } from './components/RecorderTimeLimit';
import { VideoElementWrapper } from './components/VideoElementWrapper';
import { useVideoRecorder } from './hooks/useVideoRecorder';

// oxlint-disable-next-line max-lines-per-function
export const VideoRecorder: React.FC = () => {
	const {
		data: { stream, isRecording, duration, shouldDisplayUploadButton, errorMessage },
		actions: { handleClick },
	} = useVideoRecorder();
	const { translate } = useI18n();

	return (
		<div className="flex flex-col gap-y-4">
			<div className="text-center">
				<Text as="h2" variant="heading" size="large" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition_actions.supporter_voice_video.record.heading" />
				</Text>
			</div>

			{errorMessage && <ErrorMessage />}

			<VideoElementWrapper stream={stream} isRecording={isRecording} />
			<RecorderTimeLimit duration={duration} />

			{isRecording && (
				<span
					id="hidden-force-announce-stop-btn-text"
					aria-live="assertive"
					aria-atomic="true"
					className="absolute -m-px h-px w-px overflow-hidden border-0 p-0 [white-space:nowrap] [clip-path:inset(50%)] [clip:rect(0,0,0,0)]"
				>
					{translate('corgi.petition_actions.supporter_voice_video.record.record_button.label_stop')}
				</span>
			)}
			<Button
				data-testid="start-stop-recording"
				data-qa="start-stop-recording"
				onClick={handleClick}
				variant={isRecording ? 'secondary' : 'primary'}
				className="flex-1"
				leadingIcon={
					isRecording ? (
						<StopCircleIcon size={24} className="mt-0" />
					) : (
						// Using brand dot as the record indicator
						<ChangeDot size="small" />
					)
				}
				aria-pressed={isRecording}
				aria-label={
					isRecording
						? translate('corgi.petition_actions.supporter_voice_video.record.record_button.label_stop')
						: translate('corgi.petition_actions.supporter_voice_video.record.record_button.label_start')
				}
			>
				{isRecording ? (
					<Translate value="corgi.petition_actions.supporter_voice_video.record.record_button.label_stop" />
				) : (
					<Translate value="corgi.petition_actions.supporter_voice_video.record.record_button.label_start" />
				)}
			</Button>

			{shouldDisplayUploadButton && <ChooseVideoButton isRecording={isRecording} />}

			<div className="bg-fill-weaker dark:bg-background-raised rounded-md p-4">
				<Text as="p" variant="body" size="small" className="text-typography-strong mb-4">
					<TranslateHtml value="corgi.petition_actions.supporter_voice_video.record.call_to_action_short" />
				</Text>
				<Text as="p" variant="body" size="small" className="text-typography-strong mb-4">
					<TranslateHtml value="corgi.petition_actions.supporter_voice_video.record.call_to_action_personal" />
				</Text>
				<Text as="p" variant="body" size="small" className="text-typography-strong">
					<TranslateHtml value="corgi.petition_actions.supporter_voice_video.record.call_to_action_show_up" />
				</Text>
			</div>

			<TermsDisclaimer />
		</div>
	);
};
